import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { calculateAge } from '../utils/calculateAge';
import {
  generatePetSlug,
  getPetOccurrence,
  generateHappyEndingSlug,
  getHappyEndingOccurrence,
} from '../utils/petSlug';
import useHappyEndings from '../hooks/useHappyEndingsList';
import './PetItem.css';

const PetItem = ({ pet, isLoading = false, index, allPets = [] }) => {
  const { t, i18n } = useTranslation();
  const { data: happyEndings = [] } = useHappyEndings();
  const isAdopted = pet?.status?.toLowerCase() === 'adopted';

  const imageSrc = isLoading
    ? 'img/adoption/loading.gif'
    : pet?.image_main && pet.image_main.trim() !== ''
      ? pet.image_main
      : 'img/dogimage404-funny.png';

  return (
    <div key={pet?.name || index} className="h-100">
      <div className="pf-modern-card">
        <div className="thumbnail text-center">
          <div className="square-img-container">
            {pet?._id ? (
              pet.status?.toLowerCase() === 'adopted' ? (
                <img
                  src={imageSrc}
                  className="pf-card-img"
                  alt={isLoading ? 'Loading...' : pet?.name}
                />
              ) : (
                <Link
                  to={
                    i18n.language === 'bs'
                      ? `/adoption/${generatePetSlug(pet.name, getPetOccurrence(pet, allPets))}`
                      : `/${i18n.language}/adoption/${generatePetSlug(pet.name, getPetOccurrence(pet, allPets))}`
                  }
                  state={{ pet }}
                >
                  <img
                    src={imageSrc}
                    className="pf-card-img"
                    alt={isLoading ? 'Loading...' : pet?.name}
                  />
                </Link>
              )
            ) : (
              <img
                src={imageSrc}
                className="pf-card-img"
                alt={isLoading ? 'Loading...' : pet?.name}
              />
            )}

            {/* Oznaka rezervisanog psa */}
            {!isLoading && pet?.status === 'reserved' && (
              <span className="pf-status-badge pf-badge-reserved">
                🔒 {t('adoptionPage.reserved')}
              </span>
            )}
            {/* Oznaka udomljenog psa */}
            {!isLoading && pet?.status === 'adopted' && (
              <span className="pf-status-badge pf-badge-adopted">
                🏠 {t('adoptionPage.adopted')}
              </span>
            )}
            {/* Oznaka fostered psa in DK */}
            {!isLoading && pet?.status === 'fosterDK' && (
              <span className="pf-status-badge pf-badge-foster">
                ✈️ {t('adoptionPage.fosterDK')}
              </span>
            )}
            {!isLoading && pet?.status === 'fosterCZ' && (
              <span className="pf-status-badge pf-badge-foster">
                ✈️ {t('adoptionPage.fosterCZ')}
              </span>
            )}
          </div>
          <div className={`caption-adoption ${isAdopted ? 'is-adopted' : ''}`}>
            <h6 className="adoption-header">
              {isLoading ? 'Loading...' : pet?.name}
            </h6>
            {!isAdopted && (
              <div className="pf-pills-container">
                <span className="pf-pill">
                  <i className="fas fa-venus-mars"></i>{' '}
                  {isLoading
                    ? '-'
                    : t(`common.gender.${pet.gender?.toLowerCase()}`)}
                </span>
                <span className="pf-pill">
                  <i className="fas fa-weight-hanging"></i>{' '}
                  {isLoading
                    ? '-'
                    : t(`common.size.${pet.size_category?.toLowerCase()}`)}
                </span>
                <span className="pf-pill">
                  <i className="fas fa-birthday-cake"></i>{' '}
                  {isLoading ? '-' : calculateAge(pet.birth_date, t)}
                </span>

                {!isLoading &&
                  pet?.sponsors &&
                  pet.sponsors.length > 0 &&
                  pet.sponsors.map((sponsor, idx) => (
                    <span
                      key={idx}
                      className="pf-pill sponsor-pill"
                      style={{
                        backgroundColor: '#fff9eb',
                        border: '1px solid currentColor',
                        color: '#fca311',
                      }}
                    >
                      <span style={{ color: '#444', fontWeight: '700' }}>
                        <img
                          src={
                            sponsor.sponsor_type === 'gold'
                              ? '/img/sponsor/gold_sponsor1.webp'
                              : sponsor.sponsor_type === 'silver'
                                ? '/img/sponsor/silver_sponsor.webp'
                                : '/img/sponsor/bronze_sponsor.webp'
                          }
                          alt="Paw"
                          style={{
                            width: '14px',
                            marginRight: '5px',
                            verticalAlign: 'middle',
                            marginTop: '-2px',
                          }}
                        />
                        {sponsor.name}
                      </span>
                    </span>
                  ))}
              </div>
            )}
            <div className={`text-center ${isAdopted ? 'adopted-btn-container' : 'mt-auto pt-1'}`}>
              {!isLoading &&
                (pet?._id ? (
                  pet.status?.toLowerCase() === 'adopted' ? (
                    pet.happy_ending_id ? (
                      (() => {
                        const happyEnding = happyEndings.find(
                          he => he._id === pet.happy_ending_id
                        );
                        const happyEndingSlug = happyEnding
                          ? generateHappyEndingSlug(
                              happyEnding[`title_${i18n.language}`] ||
                                happyEnding.title_en ||
                                happyEnding.title_bs,
                              getHappyEndingOccurrence(
                                happyEnding,
                                happyEndings,
                                i18n.language
                              )
                            )
                          : '';
                        return happyEnding ? (
                          <Link
                            to={
                              i18n.language === 'bs'
                                ? `/happyendings/${happyEndingSlug}`
                                : `/${i18n.language}/happyendings/${happyEndingSlug}`
                            }
                            state={{ post: happyEnding }}
                            className="btn btn-primary"
                          >
                            {t('adoptionPage.view_happy_ending')}
                          </Link>
                        ) : (
                          <button className="btn btn-primary" disabled>
                            {t('adoptionPage.loading')}
                          </button>
                        );
                      })()
                    ) : (
                      <button className="btn btn-primary" disabled>
                        {t('adoptionPage.adopted')}
                      </button>
                    )
                  ) : (
                    <Link
                      to={
                        i18n.language === 'bs'
                          ? `/adoption/${generatePetSlug(pet.name, getPetOccurrence(pet, allPets))}`
                          : `/${i18n.language}/adoption/${generatePetSlug(pet.name, getPetOccurrence(pet, allPets))}`
                      }
                      state={{ pet }}
                      className="btn btn-primary"
                    >
                      {t('adoptionPage.find_out_more')}
                    </Link>
                  )
                ) : (
                  <button className="btn btn-primary" disabled>
                    Error loading
                  </button>
                ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PetItem;
